import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import SEO from '@/components/SEO';

const EFFECTIVE_DATE = 'May 19, 2026';
const LEGAL_EMAIL = 'legal@opencardvault.com';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-3">{title}</h2>
      <div className="text-gray-700 dark:text-zinc-300 space-y-3 leading-relaxed">{children}</div>
    </section>
  );
}

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950">
      <SEO
        title="Terms of Service"
        description="Read Open Card Vault's terms of service. Understand the rules and conditions for using our trading card marketplace."
        url="/legal/terms"
      />
      <div className="bg-gradient-to-r from-brand-600 to-accent-600 text-white py-12">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="h-7 w-7" />
            <h1 className="text-3xl font-display font-bold">Terms of Service</h1>
          </div>
          <p className="text-brand-100">Effective date: {EFFECTIVE_DATE}</p>
        </div>
      </div>

      <div className="container-custom py-10 max-w-3xl">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-8">

          <p className="text-gray-700 dark:text-zinc-300 mb-8 leading-relaxed">
            These Terms of Service ("Terms") govern your access to and use of the Open Card Vault
            website, applications, trading card marketplace, community features, and related
            services (collectively, the "Service") operated by Open Card Vault ("Open Card Vault,"
            "we," "our," or "us"). By creating an account, accessing the Service, or using the
            Service, you agree to be bound by these Terms and any policies incorporated into these
            Terms by reference, including our Privacy Policy and Community Guidelines. If you do
            not agree to these Terms, you may not use the Service.
          </p>

          <Section title="1. Eligibility and Age Requirements">
            <p>You must be at least 13 years of age to create an account or use Open Card Vault.</p>
            <p>
              Users between 13 and 17 years of age may use non-transactional features made
              available to minor users, including browsing publicly available content, maintaining
              card collections, creating decks and wishlists, and using other features specifically
              made available to minor users.
            </p>
            <p>Users under 18 years of age may not:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Buy or sell items</li>
              <li>Create marketplace listings</li>
              <li>Place bids</li>
              <li>Create or participate in auctions</li>
              <li>Make or accept purchase offers</li>
              <li>Send, accept, or participate in card-for-card trades or other transactions involving the exchange of physical property</li>
              <li>Receive payments or payouts</li>
              <li>Create seller accounts</li>
              <li>Connect payment or payout accounts</li>
              <li>Enter into transactions with other users through the Service</li>
            </ul>
            <p>
              Marketplace transaction features are available only to users who are at least 18
              years of age and satisfy any identity verification, payment processor, geographic, or
              other eligibility requirements imposed by Open Card Vault or our service providers.
            </p>
            <p>
              We may request your date of birth, age confirmation, identity verification, or other
              information reasonably necessary to determine eligibility. You may not provide false
              age information, use another person's identity, allow an ineligible person to use
              your account, or attempt to circumvent age restrictions.
            </p>
            <p>
              Open Card Vault does not knowingly permit children under 13 years of age to create
              accounts or use the Service. Accounts that violate these requirements may be
              suspended or terminated.
            </p>
          </Section>

          <Section title="2. Account Registration and Responsibilities">
            <p>When you create or maintain an account, you agree to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the confidentiality and security of your login credentials</li>
              <li>Not share, sell, transfer, or permit unauthorized access to your account</li>
              <li>Notify us promptly if you suspect unauthorized access or use</li>
              <li>Accept responsibility for activity conducted through your account to the extent permitted by law</li>
              <li>Maintain only one account unless Open Card Vault expressly authorizes additional accounts</li>
            </ul>
            <p>
              You may not create accounts using automated methods, false identities, deceptive
              information, or information belonging to another person without authorization. We may
              suspend, restrict, or terminate accounts that violate these Terms, our policies,
              applicable law, or that reasonably present fraud, security, safety, or operational
              risks.
            </p>
          </Section>

          <Section title="3. Open Card Vault's Role">
            <p>
              Open Card Vault provides a platform that allows eligible users to list, discover,
              buy, sell, auction, and trade trading cards and other products permitted by our
              policies.
            </p>
            <p>Unless Open Card Vault expressly identifies itself as the seller of an item, Open Card Vault is not:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>The buyer or seller in transactions between users</li>
              <li>The owner, manufacturer, distributor, or supplier of user-listed items</li>
              <li>A professional authenticator, grading company, or appraisal service</li>
              <li>The carrier or shipping provider responsible for transporting items</li>
              <li>A party to agreements entered into directly between buyers, sellers, or traders</li>
            </ul>
            <p>
              Users are responsible for evaluating listings, transaction partners, item condition,
              authenticity, value, and suitability before entering into transactions. Our
              involvement in facilitating a transaction or dispute does not make Open Card Vault a
              buyer, seller, owner, manufacturer, authenticator, carrier, or agent of any user.
            </p>
          </Section>

          <Section title="4. Marketplace Rules">
            <p>Sellers must:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Own or have the legal right to sell each listed item</li>
              <li>Provide accurate descriptions and photographs</li>
              <li>Accurately identify card condition, edition, set, language, authenticity, alterations, defects, and other material characteristics</li>
              <li>Disclose significant flaws, damage, modifications, restoration, trimming, recoloring, counterfeiting concerns, or other information that could reasonably affect value</li>
              <li>Honor completed sales and valid auction results</li>
              <li>Ship items within the timeframe stated in the listing or, if no timeframe is stated, within five business days after receiving confirmation that shipment is authorized</li>
              <li>Package items reasonably and securely</li>
              <li>Comply with applicable laws, payment-processor requirements, tax obligations, and Open Card Vault policies</li>
            </ul>
            <p>Buyers must:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide accurate payment and shipping information</li>
              <li>Pay for purchases, accepted offers, and winning auction bids as required</li>
              <li>Review listing information before completing transactions</li>
              <li>Use the Open Card Vault dispute process in good faith</li>
              <li>Not abuse refunds, disputes, payment reversals, or chargebacks</li>
              <li>Not place bids, submit offers, or enter transactions without a genuine intention and ability to complete them</li>
            </ul>
            <p>
              Open Card Vault may remove listings, cancel transactions, restrict marketplace
              activity, delay payouts, or take other reasonable action when necessary to
              investigate suspected fraud, protect users, enforce our policies, comply with law,
              or address technical errors.
            </p>
          </Section>

          <Section title="5. Card Trades">
            <p>
              Eligible users may use Open Card Vault features to propose and accept card-for-card
              trades or other permitted exchanges of physical trading-card products. All trade
              participants must be at least 18 years old.
            </p>
            <p>Users participating in trades must:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Own or have the legal right to trade the offered items</li>
              <li>Accurately describe and photograph items</li>
              <li>Disclose known defects, alterations, authenticity concerns, and material condition issues</li>
              <li>Ship the items agreed upon within the applicable timeframe</li>
              <li>Use tracking or other proof of shipment when required by Open Card Vault policies</li>
              <li>Conduct trade communications through the Service when reasonably possible</li>
              <li>Participate honestly in investigations and dispute-resolution procedures</li>
            </ul>
            <p>
              Unless Open Card Vault expressly provides a payment protection, escrow,
              authentication, or guarantee program, Open Card Vault does not guarantee the value,
              condition, authenticity, delivery, or performance of items exchanged through trades.
            </p>
          </Section>

          <Section title="6. Auctions and Bidding">
            <p>
              Bids placed through the Service may create binding obligations to complete purchases.
              By placing a bid, you represent that you are eligible to transact and intend and are
              able to complete the purchase if your bid wins.
            </p>
            <p>Users may not:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Place bids to artificially increase prices</li>
              <li>Bid on their own listings through another account</li>
              <li>Coordinate with others to manipulate auction outcomes</li>
              <li>Engage in shill bidding, bid shielding, or similar manipulation</li>
              <li>Interfere with another user's ability to participate fairly</li>
              <li>Retract bids except where expressly permitted by Open Card Vault</li>
            </ul>
            <p>
              Sellers must honor valid auction results and may not refuse to complete a transaction
              merely because the final price is lower than expected. Open Card Vault may cancel
              bids, auctions, or resulting transactions when reasonably necessary due to fraud,
              manipulation, policy violations, technical failures, listing errors, legal
              requirements, or other circumstances affecting marketplace integrity.
            </p>
          </Section>

          <Section title="7. Prohibited Items">
            <p>Users may not list, sell, trade, distribute, or solicit items prohibited by law or Open Card Vault policies. Prohibited items include:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Counterfeit or fraudulent trading cards presented as authentic</li>
              <li>Stolen property</li>
              <li>Items the seller does not own or have the right to transfer</li>
              <li>Illegal products or services</li>
              <li>Weapons, explosives, controlled substances, illegal drugs, or other regulated products unrelated to legitimate trading-card collecting</li>
              <li>Pornographic or sexually explicit products</li>
              <li>Products that promote terrorism, violent extremist organizations, or unlawful hate-based activity</li>
              <li>Items that infringe intellectual-property rights</li>
              <li>Products subject to recall where resale is prohibited</li>
              <li>Any item Open Card Vault determines presents unreasonable legal, fraud, safety, reputational, or operational risks</li>
            </ul>
            <p>Open Card Vault may remove prohibited items and suspend or terminate accounts involved in prohibited transactions.</p>
          </Section>

          <Section title="8. Prohibited Conduct">
            <p>You may not use Open Card Vault to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Commit or facilitate fraud</li>
              <li>Misrepresent items, transactions, identities, or account information</li>
              <li>Manipulate prices, ratings, reviews, auctions, searches, or marketplace activity</li>
              <li>Engage in shill bidding or bid shielding</li>
              <li>Harass, threaten, intimidate, stalk, exploit, or abuse others</li>
              <li>Engage in discriminatory or hateful conduct prohibited by our Community Guidelines</li>
              <li>Sexually solicit, groom, exploit, or engage in inappropriate sexual communications with minors</li>
              <li>Share another person's private information without authorization</li>
              <li>Send spam, unsolicited commercial messages, malicious links, or deceptive communications</li>
              <li>Conduct transactions off-platform for the purpose of avoiding fees or protections</li>
              <li>Scrape, harvest, crawl, or systematically collect data except as expressly authorized</li>
              <li>Access accounts, systems, APIs, networks, or data without authorization</li>
              <li>Circumvent security controls, rate limits, age restrictions, bans, suspensions, or technical protections</li>
              <li>Introduce malware or malicious code</li>
              <li>Abuse reporting, dispute, refund, review, or moderation systems</li>
              <li>Impersonate another person, organization, Open Card Vault employee, moderator, or representative</li>
              <li>Violate applicable laws or regulations</li>
              <li>Infringe intellectual-property, privacy, publicity, or other rights</li>
              <li>Interfere with the operation, integrity, security, or availability of the Service</li>
            </ul>
          </Section>

          <Section title="9. Payments and Stripe">
            <p>
              Payment processing and certain seller onboarding, identity verification, fraud
              prevention, payout, and related services may be provided by Stripe or other
              payment-service providers. By using payment features, you agree to comply with the
              applicable payment provider's terms, policies, and requirements.
            </p>
            <p>
              Open Card Vault does not directly store complete payment-card numbers. Sellers may be
              required to create or connect an eligible payment-processor account before listing
              items, accepting payments, or receiving payouts.
            </p>
            <p>
              Payments and payouts may be delayed, restricted, withheld, reversed, or otherwise
              affected by payment-processor requirements, fraud investigations, disputes,
              chargebacks, legal requirements, reserves, account limitations, or other
              circumstances. Open Card Vault is not responsible for payment-processor outages,
              delays, account restrictions, verification decisions, or other actions outside our
              reasonable control.
            </p>
          </Section>

          <Section title="10. Platform Fees">
            <p>
              Open Card Vault may charge platform fees, transaction fees, service fees, listing
              fees, advertising fees, or other charges associated with use of the Service.
              Applicable fees will be disclosed before the relevant transaction or paid feature is
              completed or used.
            </p>
            <p>
              Unless otherwise stated or required by law, fees earned by Open Card Vault may be
              non-refundable. Users are responsible for reviewing applicable fees before entering
              transactions. We may change fees prospectively by providing notice through the
              Service or other reasonable methods.
            </p>
          </Section>

          <Section title="11. Taxes">
            <p>
              Users are responsible for determining and fulfilling tax obligations arising from
              their activities on Open Card Vault. Open Card Vault may collect, calculate, report,
              withhold, or remit taxes when required by applicable law. Users agree to provide
              accurate tax information when requested. Open Card Vault does not provide tax, legal,
              or accounting advice.
            </p>
          </Section>

          <Section title="12. Shipping and Delivery">
            <p>
              Sellers and traders are responsible for properly packaging and shipping items. Open
              Card Vault may establish shipping requirements, including tracking, insurance,
              signature confirmation, approved carriers, proof of shipment, or delivery
              confirmation.
            </p>
            <p>
              Open Card Vault does not operate shipping carriers and cannot guarantee delivery.
              Failure to provide required proof of shipment or delivery may affect eligibility for
              payouts, seller protection, dispute outcomes, or other marketplace protections.
            </p>
          </Section>

          <Section title="13. Refunds, Disputes, and Chargebacks">
            <p>
              Users must participate honestly and reasonably in Open Card Vault's
              dispute-resolution procedures. Buyers may submit eligible disputes within 30 days
              after the estimated delivery date unless a different period is required by law or
              stated in an applicable marketplace policy.
            </p>
            <p>
              Open Card Vault may issue or facilitate full refunds, partial refunds, transaction
              reversals, return requirements, payout adjustments, account restrictions, or other
              remedies when permitted by applicable law and our payment arrangements.
            </p>
            <p>
              Users may not initiate fraudulent, abusive, duplicative, or bad-faith disputes or
              chargebacks. Sellers may be responsible for amounts arising from refunds, reversals,
              disputes, chargebacks, penalties, or other transaction adjustments attributable to
              their transactions, conduct, or violations.
            </p>
          </Section>

          <Section title="14. User Content">
            <p>
              "User Content" includes photographs, listing descriptions, reviews, profile
              information, messages, forum posts, comments, and other materials submitted through
              the Service. You retain ownership of your User Content.
            </p>
            <p>
              By submitting User Content, you grant Open Card Vault a non-exclusive, worldwide,
              royalty-free, sublicensable, transferable license to host, store, reproduce, modify
              for technical or formatting purposes, display, distribute, and otherwise use User
              Content as reasonably necessary to operate, secure, improve, promote, and provide
              the Service.
            </p>
            <p>You represent and warrant that:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>You own your User Content or have sufficient rights to submit it</li>
              <li>Your User Content does not violate applicable law</li>
              <li>Your User Content does not infringe the rights of others</li>
              <li>Your User Content complies with these Terms and our policies</li>
            </ul>
          </Section>

          <Section title="15. Intellectual Property">
            <p>
              The Open Card Vault Service, including our software, original content, branding,
              logos, user-interface designs, and other proprietary materials, is owned by or
              licensed to Open Card Vault and is protected by applicable intellectual-property
              laws. Except as expressly permitted, you may not copy, modify, distribute, sell,
              license, reverse engineer, or create derivative works from the Service.
            </p>
            <p>
              Trading-card names, images, trademarks, logos, game content, and other third-party
              intellectual property belong to their respective owners. Open Card Vault is not
              affiliated with, sponsored by, approved by, or endorsed by any trading-card
              publisher, grading company, marketplace, or intellectual-property owner unless
              expressly stated.
            </p>
          </Section>

          <Section title="16. Copyright and Intellectual-Property Complaints">
            <p>
              If you believe content available through Open Card Vault infringes your copyright or
              other intellectual-property rights, contact:{' '}
              <a href={`mailto:${LEGAL_EMAIL}`} className="text-brand-600 dark:text-brand-400 hover:underline">
                {LEGAL_EMAIL}
              </a>
            </p>
            <p>Your notice should include:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Identification of the protected work or right allegedly infringed</li>
              <li>Identification of the allegedly infringing material and sufficient information to locate it</li>
              <li>Your contact information</li>
              <li>A statement explaining your good-faith belief that the disputed use is unauthorized</li>
              <li>A statement that the information provided is accurate and that you are authorized to act on behalf of the rights holder</li>
              <li>Your physical or electronic signature</li>
            </ul>
          </Section>

          <Section title="17. Ratings and Reviews">
            <p>
              Users may submit ratings and reviews based on genuine marketplace experiences.
              Ratings and reviews must be truthful and may not:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Contain knowingly false statements</li>
              <li>Be exchanged for undisclosed compensation or incentives</li>
              <li>Be manipulated using multiple accounts</li>
              <li>Be used to harass, threaten, extort, or retaliate against users</li>
              <li>Be submitted for transactions in which the reviewer did not participate</li>
            </ul>
          </Section>

          <Section title="18. Moderation and Enforcement">
            <p>
              Open Card Vault may investigate suspected violations of these Terms, our Community
              Guidelines, marketplace policies, or applicable law. Depending on the circumstances,
              we may:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Issue warnings</li>
              <li>Remove or restrict content</li>
              <li>Cancel listings, bids, trades, or transactions</li>
              <li>Restrict marketplace features</li>
              <li>Delay or restrict payouts where permitted</li>
              <li>Temporarily suspend accounts</li>
              <li>Permanently terminate accounts</li>
              <li>Preserve evidence</li>
              <li>Cooperate with payment processors, service providers, rights holders, regulators, or law enforcement when legally appropriate</li>
            </ul>
          </Section>

          <Section title="19. Account Suspension and Termination">
            <p>You may close your account using available account settings or by contacting us.</p>
            <p>Open Card Vault may suspend, restrict, or terminate accounts when reasonably necessary to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Enforce these Terms or our policies</li>
              <li>Protect users or the Service</li>
              <li>Prevent fraud or abuse</li>
              <li>Comply with legal obligations</li>
              <li>Address payment, security, or operational risks</li>
              <li>Respond to prolonged account inactivity</li>
              <li>Discontinue the Service or particular features</li>
            </ul>
            <p>
              Certain provisions and obligations survive account termination, including payment
              obligations, transaction responsibilities, intellectual-property provisions, dispute
              provisions, indemnification obligations, limitations of liability, and other
              provisions that by their nature should survive.
            </p>
          </Section>

          <Section title="20. Service Availability and Changes">
            <p>
              We may modify, suspend, restrict, discontinue, or introduce features of the Service.
              We do not guarantee that the Service or any particular feature will always be
              available, uninterrupted, secure, or error-free. Scheduled maintenance, technical
              failures, security incidents, third-party service outages, legal requirements, or
              other circumstances may affect availability.
            </p>
          </Section>

          <Section title="21. Disclaimer of Warranties">
            <p className="uppercase font-semibold">
              To the maximum extent permitted by law, the Service is provided "as is" and "as
              available." Open Card Vault disclaims all warranties, express, implied, or statutory,
              including warranties of merchantability, fitness for a particular purpose, title,
              non-infringement, and warranties arising from course of dealing or usage of trade.
            </p>
            <p className="uppercase font-semibold">
              Open Card Vault does not warrant or guarantee the accuracy of user listings, the
              authenticity, condition, quality, legality, or value of items, the conduct or
              performance of users, that transactions will be completed, that items will be
              delivered, or that the Service will be uninterrupted, secure, or error-free.
            </p>
            <p>Some jurisdictions do not allow certain warranty disclaimers, so some disclaimers may not apply to you.</p>
          </Section>

          <Section title="22. Limitation of Liability">
            <p className="uppercase font-semibold">
              To the maximum extent permitted by law, Open Card Vault and its owners, officers,
              directors, employees, contractors, agents, and affiliates will not be liable for
              indirect, incidental, special, exemplary, punitive, or consequential damages,
              including lost profits, lost data, loss of goodwill, business interruption, or loss
              arising from user transactions.
            </p>
            <p className="uppercase font-semibold">
              To the maximum extent permitted by law, Open Card Vault's total liability arising out
              of or relating to the Service or these Terms will not exceed the greater of: (a)
              $100; or (b) the amount of fees paid by you directly to Open Card Vault during the
              12 months preceding the event giving rise to the claim.
            </p>
            <p>Some jurisdictions do not allow certain limitations of liability, so some limitations may not apply to you.</p>
          </Section>

          <Section title="23. Indemnification">
            <p>
              To the extent permitted by law, you agree to defend, indemnify, and hold harmless
              Open Card Vault and its owners, officers, directors, employees, contractors, agents,
              and affiliates from claims, damages, losses, liabilities, judgments, costs, and
              expenses, including reasonable attorneys' fees, arising from or relating to your
              violation of these Terms or our policies, your User Content, items you list or
              trade, your violation of applicable law, your infringement of another person's
              rights, or fraud or misconduct associated with your account.
            </p>
          </Section>

          <Section title="24. Electronic Communications">
            <p>
              By creating an account or using the Service, you consent to receive notices and
              communications electronically, including through email, in-app notifications, account
              messages, or notices displayed through the Service. Electronic communications satisfy
              legal requirements that communications be provided in writing to the extent permitted
              by applicable law.
            </p>
          </Section>

          <Section title="25. Governing Law">
            <p>
              These Terms are governed by the laws of the State of Michigan, without regard to
              conflict-of-law principles, except where applicable federal law controls. Any dispute
              not subject to a valid arbitration agreement must be brought in a court of competent
              jurisdiction located in Michigan, unless applicable law requires otherwise.
            </p>
          </Section>

          <Section title="26. Dispute Resolution and Arbitration">
            <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
              <p className="font-semibold text-amber-900 dark:text-amber-300">Please read this section carefully.</p>
            </div>
            <p>
              To the extent permitted by applicable law, disputes arising from or relating to these
              Terms or the Service that cannot be resolved informally will be resolved through
              binding individual arbitration rather than in court.
            </p>
            <p>
              Before initiating arbitration, the party seeking relief must provide written notice
              describing the dispute and requested resolution and allow the other party 30 days to
              attempt to resolve the dispute informally. Arbitration will be conducted by a
              recognized arbitration provider under its applicable consumer or commercial
              arbitration rules, as appropriate.
            </p>
            <p className="uppercase font-semibold">
              To the maximum extent permitted by law, you and Open Card Vault waive the right to
              participate in class actions, class arbitrations, representative actions, or
              consolidated proceedings.
            </p>
            <p>
              Nothing in this section prevents either party from bringing an eligible claim in
              small-claims court, seeking emergency or temporary injunctive relief, reporting
              matters to government agencies, or exercising rights that cannot lawfully be waived.
            </p>
          </Section>

          <Section title="27. Changes to These Terms">
            <p>
              We may modify these Terms from time to time. When we make material changes, we will
              provide reasonable notice, which may include email, in-app notifications, or notices
              displayed through the Service. Unless a different period is required by law, material
              changes will generally become effective at least 14 days after notice. Continued use
              of the Service after updated Terms become effective constitutes acceptance of the
              updated Terms to the extent permitted by applicable law.
            </p>
          </Section>

          <Section title="28. Assignment">
            <p>
              You may not assign or transfer your rights or obligations under these Terms without
              our prior written consent. Open Card Vault may assign or transfer its rights and
              obligations in connection with a merger, acquisition, financing, reorganization, sale
              of assets, change of control, or by operation of law.
            </p>
          </Section>

          <Section title="29. Severability">
            <p>
              If any provision of these Terms is found invalid or unenforceable, that provision
              will be enforced to the maximum extent permitted by law, and the remaining provisions
              will remain in effect.
            </p>
          </Section>

          <Section title="30. Waiver">
            <p>
              Failure by Open Card Vault to enforce any provision of these Terms does not
              constitute a waiver of that provision or our right to enforce it later.
            </p>
          </Section>

          <Section title="31. Entire Agreement">
            <p>
              These Terms, together with our Privacy Policy, Community Guidelines, and any
              additional policies or terms expressly incorporated by reference, constitute the
              entire agreement between you and Open Card Vault concerning the Service.
            </p>
          </Section>

          <Section title="32. Contact Us">
            <p>Questions concerning these Terms may be sent to:</p>
            <p>
              <strong>Open Card Vault</strong><br />
              Email:{' '}
              <a href={`mailto:${LEGAL_EMAIL}`} className="text-brand-600 dark:text-brand-400 hover:underline">
                {LEGAL_EMAIL}
              </a>
            </p>
          </Section>

          <div className="border-t border-gray-200 dark:border-zinc-800 pt-6 mt-8 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-zinc-500">
            <Link to="/legal/privacy" className="hover:text-brand-600 dark:hover:text-brand-400">Privacy Policy</Link>
            <Link to="/legal/community-guidelines" className="hover:text-brand-600 dark:hover:text-brand-400">Community Guidelines</Link>
            <Link to="/guide" className="hover:text-brand-600 dark:hover:text-brand-400">How It Works</Link>
            <Link to="/contact" className="hover:text-brand-600 dark:hover:text-brand-400">Contact Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
